import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Tabs, Form, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';

// Shared chart components
import StatsHourlyChart from '../../../statistics/charts/StatsHourlyChart';
import StatsDailyChart from '../../../statistics/charts/StatsDailyChart';
import StatsPerformanceCards from '../../../statistics/charts/StatsPerformanceCards';
import StatsCurrentRate from '../../../statistics/charts/StatsCurrentRate';
import StatsStatusBreakdown from '../../../statistics/charts/StatsStatusBreakdown';
import StatsAnomaliesDisplay from '../../../statistics/charts/StatsAnomaliesDisplay';

// Actions
import {
	get_helper_stats_hourly,
	get_helper_stats_daily,
	get_helper_stats_current,
	get_helper_stats_performance,
	get_helper_stats_anomalies
} from '../../../../actions/helperStatsActions';

const HelperStatsOverview = ({ instanceId }) => {
	const dispatch = useDispatch();

	const {
		helper_stats_loading,
		helper_stats_hourly,
		helper_stats_daily,
		helper_stats_current,
		helper_stats_performance,
		helper_stats_anomalies
	} = useSelector(state => state.helperStats);

	const [hours, setHours] = useState(24);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');

	useEffect(() => {
		dispatch(get_helper_stats_performance(instanceId));
		dispatch(get_helper_stats_current(instanceId));
	}, []);

	const handleHourlyChange = (selectedHours) => {
		setHours(selectedHours);
		dispatch(get_helper_stats_hourly(instanceId, selectedHours));
	};

	const handleDailySearch = () => {
		if (startDate && endDate) {
			dispatch(get_helper_stats_daily(instanceId, startDate, endDate));
		}
	};

	return (
		<Tabs
			defaultActiveKey='overview'
			className='mt-3'
			justify
		>
			<Tab eventKey='overview' title='Overview'>
				<div className='mt-3'>
					<StatsCurrentRate rate={helper_stats_performance?.data || helper_stats_current?.data} />
					<StatsPerformanceCards performance={helper_stats_performance?.data} />
					<StatsStatusBreakdown breakdown={helper_stats_performance?.data?.status_breakdown} />
				</div>
			</Tab>

			<Tab
				eventKey='hourly'
				title='Hourly'
				onEnter={() => { if (!helper_stats_hourly?.data?.length) dispatch(get_helper_stats_hourly(instanceId, hours)); }}
			>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Select value={hours} onChange={(e) => handleHourlyChange(Number(e.target.value))}>
								<option value={12}>Last 12 hours</option>
								<option value={24}>Last 24 hours</option>
								<option value={48}>Last 48 hours</option>
							</Form.Select>
						</Col>
					</Row>
					<StatsHourlyChart data={helper_stats_hourly?.data} loading={helper_stats_loading} />
				</div>
			</Tab>

			<Tab eventKey='daily' title='Daily'>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={3}>
							<Form.Control type='date' value={startDate} onChange={(e) => setStartDate(e.target.value)} />
						</Col>
						<Col xs={3}>
							<Form.Control type='date' value={endDate} onChange={(e) => setEndDate(e.target.value)} />
						</Col>
						<Col xs={2}>
							<button className='btn submit-btn' onClick={handleDailySearch} disabled={!startDate || !endDate}>
								<i className='bi bi-search me-1'></i> Search
							</button>
						</Col>
					</Row>
					<StatsDailyChart data={helper_stats_daily?.data} loading={helper_stats_loading} />
				</div>
			</Tab>

			<Tab
				eventKey='anomalies'
				title='Anomalies'
				onEnter={() => { if (!helper_stats_anomalies?.data?.anomalies) dispatch(get_helper_stats_anomalies(instanceId)); }}
			>
				<div className='mt-3'>
					<StatsAnomaliesDisplay
						anomalies={helper_stats_anomalies?.data?.anomalies}
						summary={helper_stats_anomalies?.data?.summary}
					/>
				</div>
			</Tab>
		</Tabs>
	);
}

HelperStatsOverview.propTypes = {
	instanceId: PropTypes.string.isRequired
}

export default HelperStatsOverview;
